import { OdataSystemQueryOptions } from './odata';

type CustomQueryOptions = {
  [x: string]: any;
}

type CustomEntityPath = {
  [x: string]: number | string;
}

interface AdsOdataGeneralEntityPath {
  /**
 * one of four general scope levels, will be transformed to `Customers(${customerId})` in url if not undefined
 */
  customerId?: number | undefined;
  /**
   * one of four general scope levels, will be transformed to `Accounts(${accountId})` in url if not undefined
   */
  accountId?: number | undefined;
  /**
   * one of four general scope levels, will be transformed to `Campaigns(${campaignId})` in url if not undefined
   */
  campaignId?: number | undefined;
  /**
   * one of four general scope levels, will be transformed to `AssetGroups(${assetGroupId})` in url if not undefined
   */
   assetGroupId?: number | undefined;
}

export interface AdsOdataAPIParamsBase {
  /**
   * This property should be handled by user to get the correct URL path. There is no way to predicate the order of entity.
   *
   * The intention is place all relative things together.
   */
  entityPath: AdsOdataGeneralEntityPath & CustomEntityPath;
  queryOptions?: OdataSystemQueryOptions & CustomQueryOptions;
}

interface RequestWithPayload<Payload> {
  payload: Payload;
}

// Copy from https://stackoverflow.com/a/61132308
type DeepPartial<T> = T extends object ? {
  [P in keyof T]?: DeepPartial<T[P]>;
} : T;

export type PostOdataAdsAPIParams<Payload extends any> = AdsOdataAPIParamsBase & RequestWithPayload<Payload>;

export type PatchOdataAdsAPIParams<Payload extends any> = AdsOdataAPIParamsBase & RequestWithPayload<DeepPartial<Payload>>;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export type GetOdataAdsAPIParams<Payload extends any> = AdsOdataAPIParamsBase;


// @example
// baseUrl: `https://app1.ui.ads-int.microsoft.com:12000/ODataApi/Campaign/V2/`
// path: `ProductListingGroups/Default.GridData`
// adsOdataParams: { customerId:"123456789", queryStrings:{$top:1} }
// --> https://app1.ui.ads-int.microsoft.com:12000/ODataApi/Campaign/V2/Customers(123456789)/ProductListingGroups/Default.GridData&$top=1
export function getFullUrlOfAdsOdata(baseUrl: string, path: string, adsOdataParams: AdsOdataAPIParamsBase) {
  const {
    queryOptions, entityPath: {
      customerId, accountId, campaignId, assetGroupId,
    },
  } = adsOdataParams;

  const scopeLevelPath = [
    '/',
    customerId ? `Customers(${customerId})/` : '',
    accountId ? `Accounts(${accountId})/` : '',
    campaignId ? `Campaigns(${campaignId})/` : '',
    assetGroupId ? `AssetGroups(${assetGroupId})/` : '',
  ].join('');

  const queryString = Object.entries(queryOptions ?? []).reduce((cur, [key, value]) => {
    if (value) {
      cur.set(key, value.toString());
    }
    return cur;
  }, new URLSearchParams()).toString();

  // omit `baseUrl`, for we are using a special fetch
  const finalUrl = `${scopeLevelPath}${path}${queryString ? `?${queryString}` : ''}`;
  return finalUrl;
}

/**
 * @example
 * interface CustomEntityPath1{
 *   custom1: number | string;
 * }
 *
 * type FinalType = PostOdataAdsAPIParams<XX> & WithEntityPath<CustomEntityPath1>
 */
export interface WithEntityPath<T> {
  entityPath: T;
}
